/**
 * PlanView — 2D top-down editor for the Size tab.
 * Renders the room footprint, equipment rectangles (drag-to-reposition with
 * smart-guide snapping), cable-tray polygons (selectable), door/window
 * openings overlay, and the click-to-edit dimension overlay.
 */
import { useRef, useState } from "react";
import PlanDimensions from "@/components/PlanDimensions";
import { genId, wallFlushMargin } from "@/components/elevation/wallGeometry";
import { toast } from "sonner";
import OpeningsOverlay from "./OpeningsOverlay";
import PlanPartitions from "./PlanPartitions";
import { orientedDimensions } from "@/lib/orientation";
import { fmtLenShort } from "@/lib/units";

/** Minimum cursor travel (in SCREEN pixels — NOT room inches) before a
 *  click upgrades to a drag. Combined with a press-and-hold time gate
 *  below, this prevents accidental drags on tiny rects (e.g., PNL-002
 *  in a 900″ Eroom renders at 6 × 20 CSS-px — easy to nudge >12 px on
 *  a fast click). 20 px is the standard "click vs drag" delta used by
 *  Chromium / Figma / Sketch. */
const DRAG_THRESHOLD_PX = 22;

/** Minimum hold duration before drag can arm. A normal click is <100 ms;
 *  this gate makes sure a fast click that happens to drift 20+ px still
 *  registers as a SELECT, not a DRAG. */
const DRAG_HOLD_MS = 140;

/** Minimum NUMBER of `mousemove` events that must fire after `mousedown`
 *  before drag can arm. v1.80 — a click that wobbles 22 px and holds
 *  140 ms but produces ZERO real cursor movement (browser jitter / touch-
 *  click on a high-DPI display) must NOT arm. Requiring at least 3
 *  distinct mousemove dispatches guarantees the user actually dragged. */
const DRAG_MIN_MOVES = 3;

export default function PlanView({
  project, onItemMove, trayMode, onTrayAdd,
  partitionMode, onPartitionAdd, onPartitionUpdate, onPartitionDelete,
  equipmentMode, pickedItem, onEquipmentPlace,
  showDims, selection, setSelection, onEditDimension,
  recentlyEdited, onPreviewDimension, previewPos,
  onTrayEndMove,
  installSide = "all",
  thickWalls = false,
}) {
  // `_in` fields are always stored as inches regardless of the project's
  // unit_system (metric is a UI display preference). Toggling units never
  // rescales stored values.
  const L = project.length_in || 0;
  const W = project.width_in  || 0;
  /* v1.81 — live preview position override. While the user is typing into
   * a dim counter, SizeTab feeds us a `previewPos = {id, x_in, y_in}` for
   * the item being edited. We splice that in so the rect AND the dim
   * arrows glide to the previewed location as each keystroke commits.
   * On Enter/✓ the real `equipment_list` is mutated, on Escape/blur the
   * preview is cleared and the rect glides back. */
  const rawItems = (project.equipment_list || []).filter((i) => i?.position);
  const items = previewPos
    ? rawItems.map((it) => it.id === previewPos.id
        ? { ...it, position: { ...it.position, x_in: previewPos.x_in, y_in: previewPos.y_in } }
        : it)
    : rawItems;
  const trays = project.cable_trays || [];
  const openings = project.wall_openings || [];
  const svgRef = useRef(null);
  const [drag, setDrag] = useState(null);
  const [pendingTray, setPendingTray] = useState(null);
  // v1.84 — first click of a partition draw; null when no partition in progress.
  const [pendingPartition, setPendingPartition] = useState(null);
  // v2.11 — live cursor position while drawing a partition (for rubber-band
  // preview with straight-line snap). Updated on every mousemove inside
  // partition mode. Cleared on commit / cancel.
  const [partCursor, setPartCursor] = useState(null);
  // v2.11 — partition endpoint drag state. Mirrors the tray endpoint
  // drag pattern. `endpoint` is "A" (x1,y1) or "B" (x2,y2).
  const [partEndDrag, setPartEndDrag] = useState(null);
  // v1.87 — horizontal-tray endpoint drag state. Mirrors the equipment
  // drag pattern: armed only after a real cursor movement, so single
  // clicks don't reposition by accident.
  const [trayEndDrag, setTrayEndDrag] = useState(null);
  /** Smart-guide state — set live during equipment drag to surface
   *  alignment opportunities with other items / walls. Snap radius = 6″. */
  const [guides, setGuides] = useState({ x: null, y: null });
  // v2.04 — per-wall shell visibility. Each wall starts ON (solid).
  // Right-click a wall in the plan view to toggle it OFF so you can see
  // the equipment / trays mounted on the exterior face of that wall.
  const [wallVisibility, setWallVisibility] = useState({
    A: true, B: true, C: true, D: true,
  });
  const toggleWall = (w) => setWallVisibility((v) => ({ ...v, [w]: !v[w] }));

  if (L <= 0 || W <= 0) {
    return (
      <div className="text-gray-500 text-sm font-mono py-8 text-center">
        Enter room length and width to enable layout.
      </div>
    );
  }

  const viewW = 960;
  // v2.05 — padding bumped 60 → 80 so the thicker (up to 60 px) shell
  // walls always fit comfortably inside the SVG viewport with room for
  // wall labels too.
  const padding = 80;
  const scale = (viewW - padding * 2) / L;
  const viewH = W * scale + padding * 2;
  const toSvg = (x, y) => ({ x: padding + x * scale, y: viewH - padding - y * scale });
  // v2.03 — enclosure-shell geometry hoisted above the render path so the
  // values are computed once per render (rather than inside an IIFE that
  // returns a fresh closure each frame). This stabilises the strokeWidth
  // and corner positions so the shell never re-mounts during drag.
  const shell_wt_in = Math.max(0.5, Number(project.wall_thickness_in ?? 6));
  // v2.06 — `thickWalls` toggle from SizeTab lets the user A/B test
  // realistic-thickness walls (14 px / scaled to wall_thickness_in) vs
  // bold "diagrammatic" walls (40-50 px). Both share the same solid
  // shell + roof-quadrant cover-up logic.
  const shellWallPx = thickWalls
    ? Math.max(40, Math.min(60, shell_wt_in * scale * 4))
    : Math.max(14, Math.min(60, shell_wt_in * scale));
  const shellInnerPx = Math.max(2, shellWallPx / 3);
  const shellX0 = padding, shellY0 = padding;
  const shellX1 = padding + L * scale, shellY1 = padding + W * scale;
  const shellCornerR = Math.max(5, shellWallPx * 0.55);
  const svgToRoom = (sx, sy) => {
    const margin = wallFlushMargin((project.tray_defaults?.width_in) || 12);
    return {
      x: Math.max(margin, Math.min(L - margin, (sx - padding) / scale)),
      y: Math.max(margin, Math.min(W - margin, (viewH - padding - sy) / scale)),
    };
  };
  const onMouseDown = (e, item) => {
    if (trayMode) return;
    /* v1.77 — bail if the click landed on the dimension counter rather
     * than the equipment rect. PlanDimensions is rendered as a sibling
     * <g> AFTER equipment groups; events from foreignObject HTML inputs
     * can occasionally re-target due to React's synthetic event system
     * (particularly on quick mouseDown→drift→mouseUp sequences). The
     * `data-noequip-drag` attribute on the counter <g> roots catches all
     * descendant elements (label rect, input, ✓ button, foreignObject).
     * tagName checks for INPUT / BUTTON are belt-and-braces. */
    const t = e.target;
    if (t && (
      (t.closest && t.closest('[data-noequip-drag]')) ||
      (t.closest && t.closest('[data-testid^="dim-label-"]')) ||
      (t.closest && t.closest('[data-testid^="dim-input-"]')) ||
      t.tagName === "INPUT" ||
      t.tagName === "BUTTON" ||
      t.tagName === "foreignObject"
    )) {
      return;
    }
    setSelection && setSelection({ kind: "equipment", id: item.id });
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const local = pt.matrixTransform(svg.getScreenCTM().inverse());
    /* `armed: false` until the cursor moves past the threshold — this is
     * the fix for the "first-click moves the item" bug. We track BOTH the
     * SVG-local start point (for plan-inch math) and the screen pixel
     * start point (for the threshold check, which is room-size independent). */
    setDrag({
      id: item.id,
      startX: local.x, startY: local.y,
      startScreenX: e.clientX, startScreenY: e.clientY,
      startTime: e.timeStamp,
      origX: item.position.x_in, origY: item.position.y_in,
      armed: false,
      moveCount: 0,
    });
    e.stopPropagation();
  };
  const onMouseMove = (e) => {
    if (!drag) return;
    // Threshold check is in SCREEN pixels — independent of room scale.
    if (!drag.armed) {
      const dxScreen = e.clientX - drag.startScreenX;
      const dyScreen = e.clientY - drag.startScreenY;
      const heldMs = e.timeStamp - (drag.startTime || 0);
      const moves = (drag.moveCount || 0) + 1;
      // Tally the mousemove first so we can enforce a minimum-real-move
      // gate (v1.80) — kills "first-click moves the rect" caused by a
      // single jittery mousemove that crosses 22 px without the user
      // having actually dragged anywhere.
      setDrag((cur) => (cur ? { ...cur, moveCount: moves } : cur));
      // Need ALL THREE: (a) cursor moved past threshold, (b) mouse held
      // long enough, (c) at least DRAG_MIN_MOVES mousemove dispatches.
      if (Math.hypot(dxScreen, dyScreen) < DRAG_THRESHOLD_PX) return;
      if (heldMs < DRAG_HOLD_MS) return;
      if (moves < DRAG_MIN_MOVES) return;
      // Re-baseline so the threshold travel does NOT translate into actual
      // displacement on the first armed frame (fixes the "click snaps the
      // rect" perception on flipped equipment).
      const svg = svgRef.current;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX; pt.y = e.clientY;
      const local = pt.matrixTransform(svg.getScreenCTM().inverse());
      const orig = items.find((i) => i.id === drag.id);
      if (!orig) { setDrag(null); return; }
      setDrag({
        id: drag.id, armed: true,
        startX: local.x, startY: local.y,
        startScreenX: e.clientX, startScreenY: e.clientY,
        origX: orig.position.x_in, origY: orig.position.y_in,
      });
      return;
    }
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const local = pt.matrixTransform(svg.getScreenCTM().inverse());
    const dx = (local.x - drag.startX) / scale;
    const dy = -(local.y - drag.startY) / scale;
    const it = items.find((i) => i.id === drag.id);
    if (!it) return;
    // Use orientedDimensions for clamps + smart-guides so flipped equipment
    // (where pos.w_in/h_in lags behind the new axis_map) doesn't get
    // clamped against the stale footprint.
    const _od = orientedDimensions(it);
    const dragW = _od.wallSize;
    const dragH = _od.thickness;
    let newX = Math.max(0, Math.min(L - dragW, drag.origX + dx));
    let newY = Math.max(0, Math.min(W - dragH, drag.origY + dy));

    // ── Smart guides + snap ──────────────────────────────────────
    // v2.16 — SNAP_RADIUS was 6 INCHES, which translates to ~12-20 SVG
    // pixels in a typical room. Equipment placed within 6" of Wall B / D
    // (right / left walls) got "spring-back" snapped to the wall on the
    // first jittery mousemove past the drag threshold, shifting it 1-6
    // inches — visible to the user as a 3-20 px drift on every click /
    // tiny drag. Moving to a pixel-based radius (4 SVG px / scale) keeps
    // the snap feel consistent across room sizes and confines it to a
    // tight 4-px screen zone where the user is unambiguously aiming for
    // the wall, not where they happen to land after a click.
    const SNAP_RADIUS = 4 / scale;
    const cxNew = newX + dragW / 2;
    const cyNew = newY + dragH / 2;
    const candX = [
      { v: 0,     tag: "Wall D",      kind: "wall" },
      { v: L,     tag: "Wall B",      kind: "wall" },
      { v: L / 2, tag: "room centre", kind: "wall" },
    ];
    const candY = [
      { v: 0,     tag: "Wall C",      kind: "wall" },
      { v: W,     tag: "Wall A",      kind: "wall" },
      { v: W / 2, tag: "room centre", kind: "wall" },
    ];
    items.forEach((other) => {
      if (other.id === drag.id || !other.position) return;
      const op = other.position;
      // v1.69 — snap to the oriented bbox edges, not the stale pos.w_in/h_in.
      const _otherOd = orientedDimensions(other);
      const ow = _otherOd.wallSize, od = _otherOd.thickness;
      candX.push({ v: op.x_in,           tag: other.tag,        kind: "item" });
      candX.push({ v: op.x_in + ow,      tag: other.tag,        kind: "item" });
      candX.push({ v: op.x_in + ow / 2,  tag: `${other.tag} ⊥`, kind: "item" });
      candY.push({ v: op.y_in,           tag: other.tag,        kind: "item" });
      candY.push({ v: op.y_in + od,      tag: other.tag,        kind: "item" });
      candY.push({ v: op.y_in + od / 2,  tag: `${other.tag} ⊥`, kind: "item" });
    });
    const findBest = (cands, edges) => {
      let best = null;
      cands.forEach((c) => {
        edges.forEach((edge) => {
          const delta = c.v - edge.v;
          if (Math.abs(delta) < SNAP_RADIUS) {
            if (best == null || Math.abs(delta) < Math.abs(best.delta)) {
              best = { delta, candV: c.v, edge, cand: c };
            }
          }
        });
      });
      return best;
    };
    const xEdges = [
      { name: "left",   v: newX },
      { name: "centre", v: cxNew },
      { name: "right",  v: newX + dragW },
    ];
    const yEdges = [
      { name: "top",    v: newY },
      { name: "centre", v: cyNew },
      { name: "bottom", v: newY + dragH },
    ];
    const bestX = findBest(candX, xEdges);
    const bestY = findBest(candY, yEdges);
    if (bestX) newX += bestX.delta;
    if (bestY) newY += bestY.delta;
    newX = Math.max(0, Math.min(L - dragW, newX));
    newY = Math.max(0, Math.min(W - dragH, newY));
    setGuides({
      x: bestX ? { v: bestX.candV, tag: bestX.cand.tag } : null,
      y: bestY ? { v: bestY.candV, tag: bestY.cand.tag } : null,
    });

    const conflicts = [];
    if (newX + dragW > L || newY + dragH > W) conflicts.push(`'${it.tag || "item"}' is outside the room.`);
    for (const other of items) {
      if (other.id === drag.id || !other.position) continue;
      const _otherOd = orientedDimensions(other);
      const a = { x: newX, y: newY, w: dragW, h: dragH };
      const b = { x: other.position.x_in, y: other.position.y_in, w: _otherOd.wallSize, h: _otherOd.thickness };
      const overlap = !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
      if (overlap) conflicts.push(`'${it.tag || "item"}' overlaps '${other.tag || "item"}'.`);
    }
    onItemMove(drag.id, newX, newY, conflicts);
  };
  const onMouseUp = () => { setDrag(null); setGuides({ x: null, y: null }); setTrayEndDrag(null); setPartEndDrag(null); };

  /* v1.87 — handle tray endpoint dragging. Fires from the outer svg's
   * onMouseMove so the cursor doesn't need to stay over the bubble. */
  const onSvgMouseMove = (e) => {
    onMouseMove(e);
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const local = pt.matrixTransform(svg.getScreenCTM().inverse());
    // v2.11 — track cursor while drawing a partition for the rubber-band.
    if (partitionMode && pendingPartition) {
      const room = svgToRoom(local.x, local.y);
      setPartCursor({
        x: Math.max(0, Math.min(L, Math.round(room.x))),
        y: Math.max(0, Math.min(W, Math.round(room.y))),
        shift: !!e.shiftKey,
      });
    }
    // v2.11 — partition endpoint drag. Straight-line constraint: when an
    // endpoint is moved, snap to the axis of the OTHER endpoint unless
    // Shift is held. This keeps partitions orthogonal by default.
    if (partEndDrag) {
      const part = (project.room_partitions || []).find((p) => p.id === partEndDrag.partitionId);
      if (part) {
        const room = svgToRoom(local.x, local.y);
        let nx = Math.max(0, Math.min(L, Math.round(room.x)));
        let ny = Math.max(0, Math.min(W, Math.round(room.y)));
        const otherX = partEndDrag.endpoint === "A" ? part.x2 : part.x1;
        const otherY = partEndDrag.endpoint === "A" ? part.y2 : part.y1;
        if (!e.shiftKey) {
          const ddx = Math.abs(nx - otherX);
          const ddy = Math.abs(ny - otherY);
          if (ddx >= ddy) ny = otherY;
          else            nx = otherX;
        }
        const patch = partEndDrag.endpoint === "A"
          ? { x1: nx, y1: ny }
          : { x2: nx, y2: ny };
        onPartitionUpdate && onPartitionUpdate(part.id, patch);
      }
    }
    if (!trayEndDrag) return;
    const room = svgToRoom(local.x, local.y);
    const clampedX = Math.max(0, Math.min(L, Math.round(room.x)));
    const clampedY = Math.max(0, Math.min(W, Math.round(room.y)));
    onTrayEndMove && onTrayEndMove(trayEndDrag.trayId, trayEndDrag.endpoint, clampedX, clampedY);
  };
  const onSvgClick = (e) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const local = pt.matrixTransform(svg.getScreenCTM().inverse());
    // v2.01 — Equipment mode placement. When the user has picked an
    // unplaced equipment item and clicks anywhere on the plan view, hand
    // the room-inch coords back to SizeTab which decides floor vs roof
    // based on installSide.
    if (equipmentMode && pickedItem && onEquipmentPlace) {
      const room = svgToRoom(local.x, local.y);
      const x = Math.max(0, Math.min(L, Math.round(room.x)));
      const y = Math.max(0, Math.min(W, Math.round(room.y)));
      onEquipmentPlace(x, y);
      return;
    }
    if (partitionMode) {
      const room = svgToRoom(local.x, local.y);
      // Clamp to inside-room (no extra margin needed — partitions are
      // free-form inside the enclosure).
      const x = Math.max(0, Math.min(L, Math.round(room.x)));
      const y = Math.max(0, Math.min(W, Math.round(room.y)));
      if (!pendingPartition) { setPendingPartition({ x, y }); return; }
      // v2.11 — straight-line snap on commit. Default is axis-aligned
      // (orthogonal) which matches how interior partitions are built in
      // real electrical rooms. Hold Shift to draw a free-form diagonal.
      let x2 = x, y2 = y;
      if (!e.shiftKey) {
        const ddx = Math.abs(x - pendingPartition.x);
        const ddy = Math.abs(y - pendingPartition.y);
        if (ddx >= ddy) y2 = pendingPartition.y;
        else            x2 = pendingPartition.x;
      }
      // 2nd click — commit a new partition. Skip if start≈end (no length).
      const dx = x2 - pendingPartition.x, dy = y2 - pendingPartition.y;
      const lenIn = Math.hypot(dx, dy);
      if (lenIn < 12) {
        // v1.86 — silent discard was confusing (iter_60 review).
        toast.error(`Partition too short (${Math.round(lenIn)}″) — minimum 12″. Try again.`);
        setPendingPartition(null);
        setPartCursor(null);
        return;
      }
      // Default door: centered, 36" wide × 84" tall. v2.13 — stored as
      // a single-element `doors` array so editing UX matches windows.
      const seg = {
        id: genId(),
        x1: pendingPartition.x, y1: pendingPartition.y, x2: x2, y2: y2,
        height_in: project?.height_in || project?.depth_in || 120,
        doors: [{ id: genId(), offset: 0.5, width_in: 36, height_in: 84 }],
        windows: [],
        penetrations: [],
      };
      onPartitionAdd && onPartitionAdd(seg);
      setPendingPartition(null);
      setPartCursor(null);
      return;
    }
    if (!trayMode) return;
    const room = svgToRoom(local.x, local.y);
    if (!pendingTray) setPendingTray(room);
    else {
      onTrayAdd({
        id: genId(),
        kind: "run",
        points: [[Math.round(pendingTray.x), Math.round(pendingTray.y)],
                 [Math.round(room.x),         Math.round(room.y)]],
      });
      setPendingTray(null);
    }
  };

  return (
    <svg ref={svgRef} viewBox={`0 0 ${viewW} ${viewH}`}
      className={`w-full h-auto bg-[#050608] ${(trayMode || partitionMode || (equipmentMode && pickedItem)) ? "cursor-crosshair" : ""}`}
      onMouseMove={onSvgMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
      onClick={onSvgClick}
      data-testid="plan-view-svg">
      <defs>
        <pattern id="planGrid" width="24" height="24" patternUnits="userSpaceOnUse">
          <path d="M 24 0 L 0 0 0 24" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
        </pattern>
        <pattern id="hatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="#FFCC00" strokeOpacity="0.45" strokeWidth="1" />
        </pattern>
        {/* v2.14 — clip rect for partitions. Ensures any partition that
            (somehow) extends past the room boundary is visually trimmed
            to the inside of the enclosure. The endpoint-drag, draw and
            numeric inputs already clamp at the data level — this is a
            safety net for legacy / imported partitions. */}
        <clipPath id="planRoomClip">
          <rect x={padding} y={padding} width={L * scale} height={W * scale} />
        </clipPath>
      </defs>
      <rect width={viewW} height={viewH} fill="url(#planGrid)" />
      {/* v2.10 — cyan room boundary (single thin stroke). The thick gray
          shell border was removed because it duplicated info already
          shown in 3D + elevation views. */}
      <rect x={padding} y={padding} width={L * scale} height={W * scale}
            fill="none" stroke="#00E5FF" strokeWidth="1.5"
            data-testid="plan-room-boundary" />
      <text x={padding} y={padding - 8} fill="#9CA3AF" fontFamily="JetBrains Mono" fontSize="11">{`L = ${fmtLenShort(L, project)} · W = ${fmtLenShort(W, project)}`}</text>

      {/* Wall labels A / B / C / D */}
      <g fontFamily="JetBrains Mono" fontSize="11" fontWeight="800">
        <rect x={viewW / 2 - 24} y={padding - 22} width="48" height="16" fill="#FFCC00" />
        <text x={viewW / 2} y={padding - 10} fill="#0A0C10" textAnchor="middle">WALL A</text>
        <rect x={viewW - padding + 4} y={viewH / 2 - 8} width="42" height="16" fill="#FFCC00" />
        <text x={viewW - padding + 25} y={viewH / 2 + 4} fill="#0A0C10" textAnchor="middle">WALL B</text>
        <rect x={viewW / 2 - 24} y={viewH - padding + 6} width="48" height="16" fill="#FFCC00" />
        <text x={viewW / 2} y={viewH - padding + 18} fill="#0A0C10" textAnchor="middle">WALL C</text>
        <rect x={padding - 46} y={viewH / 2 - 8} width="42" height="16" fill="#FFCC00" />
        <text x={padding - 25} y={viewH / 2 + 4} fill="#0A0C10" textAnchor="middle">WALL D</text>
      </g>

      <text x={viewW - padding} y={viewH - 4} fill="#6B7280" fontFamily="JetBrains Mono" fontSize="10" textAnchor="end">FRONT (entrance) · Wall C</text>
      <text x={viewW - padding} y={padding - 8} fill="#6B7280" fontFamily="JetBrains Mono" fontSize="10" textAnchor="end">BACK wall · Wall A</text>

      <OpeningsOverlay openings={openings} L={L} W={W} scale={scale} padding={padding} viewW={viewW} viewH={viewH} />

      {/* v2.20 — Partitions block extracted to /size/PlanPartitions.jsx. */}
      <PlanPartitions
        project={project}
        scale={scale}
        toSvg={toSvg}
        fmtLenShort={fmtLenShort}
        selection={selection}
        setSelection={setSelection}
        partitionMode={partitionMode}
        trayMode={trayMode}
        equipmentMode={equipmentMode}
        onPartitionDelete={onPartitionDelete}
        partEndDrag={partEndDrag}
        setPartEndDrag={setPartEndDrag}
        pendingPartition={pendingPartition}
        partCursor={partCursor}
      />

      {/* Clearance hatching */}
      {items.map((it, i) => {
        const cz = it.position.clearance;
        if (!cz) return null;
        const tl = toSvg(cz.x, cz.y + cz.h);
        return <rect key={`cl-${i}`} x={tl.x} y={tl.y}
                     width={cz.w * scale} height={cz.h * scale}
                     fill="url(#hatch)" stroke="#FFCC00" strokeOpacity="0.6" strokeDasharray="4 3" />;
      })}

      {/* Trays */}
      {trays.map((t, i) => {
        if (!t.points || t.points.length === 0) return null;
        // v1.98 — installation-side filter also applies to trays/conduits.
        // Trays default to "interior" if unset (backwards compat). Exterior
        // and through trays follow the same filtering rules as equipment.
        const trayInst = t.installation_side || "interior";
        // v2.13 — same filter behaviour as equipment: `all` hides basement,
        // `basement` shows only basement, etc.
        if (installSide === "interior" && trayInst !== "interior") return null;
        if (installSide === "exterior" && trayInst !== "exterior") return null;
        if (installSide === "basement" && trayInst !== "basement") return null;
        if (installSide === "all"      && trayInst === "basement") return null;
        // Drops only have one point; for consistency reuse it as point B
        // so all the downstream math (toSvg, dx/dy, midX/midY) is safe.
        const p0 = t.points[0];
        const p1 = t.points[1] || p0;
        const a = toSvg(p0[0], p0[1]);
        const b = toSvg(p1[0], p1[1]);
        const dx = p1[0] - p0[0];
        const dy = p1[1] - p0[1];
        const isVertical = (t.kind === "drop");
        const idLabel = t.id_display || t.tag || `CT-${String(i + 1).padStart(3, "0")}`;
        const midX = (a.x + b.x) / 2;
        const midY = (a.y + b.y) / 2;
        const onClickRename = (ev) => {
          ev.stopPropagation();
          if (!ev.altKey && ev.button !== 2) {
            setSelection && setSelection({ kind: "tray", id: t.id });
            return;
          }
          const next = (typeof window !== "undefined" && window.prompt)
            ? window.prompt("Tray ID:", idLabel) : null;
          if (next === null) return;
          if (next === "") return;
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("eengdes:tray-rename", { detail: { id: t.id, newId: next.trim() } }));
          }
        };
        if (isVertical) {
          const x_in = t.points[0][0];
          const y_in = t.points[0][1];
          const dLeft  = x_in;
          const dRight = L - x_in;
          const dFront = y_in;
          const dBack  = W - y_in;
          const minD = Math.min(dLeft, dRight, dFront, dBack);
          const wallAxisIsX = (minD === dLeft || minD === dRight);
          const trayW_in = (project.tray_defaults?.width_in || 12);
          const trayD_in = (project.tray_defaults?.height_in || 4);
          const rectW = (wallAxisIsX ? trayD_in : trayW_in) * scale;
          const rectH = (wallAxisIsX ? trayW_in : trayD_in) * scale;
          const rectX = a.x - rectW / 2;
          const rectY = a.y - rectH / 2;
          return (
            <g key={t.id || i} onClick={onClickRename} style={{ cursor: "pointer" }} data-testid={`plan-tray-${idLabel}`}>
              <rect x={rectX} y={rectY} width={rectW} height={rectH}
                    fill="#FFA500" fillOpacity="0.55" stroke="#FFCC00" strokeWidth="1.5" />
              <line x1={rectX} y1={rectY} x2={rectX + rectW} y2={rectY + rectH} stroke="#0A0C10" strokeWidth="1.2" />
              <line x1={rectX + rectW} y1={rectY} x2={rectX} y2={rectY + rectH} stroke="#0A0C10" strokeWidth="1.2" />
              <text x={rectX + rectW + 4} y={rectY - 2} fill="#FFCC00" fontFamily="JetBrains Mono" fontSize="10">{idLabel} ↕ vert</text>
              <text x={rectX - 6} y={rectY - 2}  fill="#a3e635" fontFamily="JetBrains Mono" fontSize="9" textAnchor="end" fontWeight="700">A</text>
              <text x={rectX - 6} y={rectY + rectH + 9} fill="#FF66E0" fontFamily="JetBrains Mono" fontSize="9" textAnchor="end" fontWeight="700">B</text>
            </g>
          );
        }
        // Horizontal run
        const trayW_in = (project.tray_defaults?.width_in || 12);
        const halfW_px = (trayW_in / 2) * scale;
        const angle = Math.atan2(b.y - a.y, b.x - a.x);
        const nx = -Math.sin(angle);
        const ny =  Math.cos(angle);
        const corners = [
          [a.x + nx * halfW_px, a.y + ny * halfW_px],
          [b.x + nx * halfW_px, b.y + ny * halfW_px],
          [b.x - nx * halfW_px, b.y - ny * halfW_px],
          [a.x - nx * halfW_px, a.y - ny * halfW_px],
        ].map((p) => p.join(",")).join(" ");
        /* v1.87 — draggable endpoint bubbles. mousedown on A or B starts a
         * tray-end drag; outer svg onMouseMove updates the matching point
         * in real time via onTrayEndMove. Stop propagation so the click
         * doesn't bubble up and start a tray-mode draw. */
        const startEndDrag = (endpoint) => (ev) => {
          ev.stopPropagation();
          if (ev.preventDefault) ev.preventDefault();
          setTrayEndDrag({ trayId: t.id, endpoint });
          setSelection && setSelection({ kind: "tray", id: t.id });
        };
        const draggingA = trayEndDrag?.trayId === t.id && trayEndDrag?.endpoint === "A";
        const draggingB = trayEndDrag?.trayId === t.id && trayEndDrag?.endpoint === "B";
        return (
          <g key={t.id || i} data-testid={`plan-tray-${idLabel}`}>
            <polygon points={corners} fill="#FFA500" fillOpacity="0.25" stroke="#FFA500" strokeWidth="1.5"
                     onClick={onClickRename} style={{ cursor: "pointer" }} />
            <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#FFCC00" strokeWidth="1.5" strokeDasharray="3 4"
                  pointerEvents="none" />
            <text x={midX} y={midY - halfW_px - 4} fill="#FFCC00" fontFamily="JetBrains Mono" fontSize="10"
                  textAnchor="middle" pointerEvents="none">{idLabel}</text>
            {/* v1.95 — endpoints simplified to small circles matching the
                tray's ORANGE colour, in the same style as the elevation-view
                run endpoints. Turn yellow while actively dragged. v1.97
                adds keyboard arrow-key nudging: click an endpoint to focus
                it, then ← → ↑ ↓ nudges by 1″ (Shift = 12″). */}
            {[
              { p: a, key: "A", isDragging: draggingA, ep: p0 },
              { p: b, key: "B", isDragging: draggingB, ep: p1 },
            ].map(({ p, key, isDragging: dragging, ep }) => (
              <g key={`end-${key}`}
                 tabIndex={0}
                 onMouseDown={(ev) => { ev.currentTarget.focus(); startEndDrag(key)(ev); }}
                 onTouchStart={startEndDrag(key)}
                 onKeyDown={(ev) => {
                   const step = ev.shiftKey ? 12 : 1;
                   let dx = 0, dy = 0;
                   if (ev.key === "ArrowLeft")  dx = -step;
                   else if (ev.key === "ArrowRight") dx =  step;
                   else if (ev.key === "ArrowUp")    dy = -step;
                   else if (ev.key === "ArrowDown")  dy =  step;
                   else return;
                   ev.preventDefault();
                   ev.stopPropagation();
                   const nx = Math.max(0, Math.min(L, ep[0] + dx));
                   const ny = Math.max(0, Math.min(W, ep[1] + dy));
                   onTrayEndMove && onTrayEndMove(t.id || idLabel, key, nx, ny);
                 }}
                 style={{ cursor: "grab", touchAction: "none", outline: "none" }}
                 data-testid={`plan-tray-${idLabel}-end-${key}`}>
                <circle cx={p.x} cy={p.y} r={dragging ? 7 : 5}
                        fill={dragging ? "#FFCC00" : "#FFA500"}
                        stroke="#0A0C10" strokeWidth="1.2" />
              </g>
            ))}
            {/* v1.96 — drag label matches the vertical-drop format
                `CT-XXX · A 120,45″` showing the live X,Y of the active
                endpoint in room inches. */}
            {(draggingA || draggingB) && (() => {
              const which = draggingA ? "A" : "B";
              const ep = draggingA ? p0 : p1;
              const epSvg = draggingA ? a : b;
              return (
                <text x={epSvg.x + 10} y={epSvg.y - 8}
                      fill="#FFA500" fontFamily="JetBrains Mono"
                      fontSize="10" fontWeight="700" pointerEvents="none">
                  {idLabel} · {which} {Math.round(ep[0])},{Math.round(ep[1])}&quot;
                </text>
              );
            })()}
          </g>
        );
      })}
      {pendingTray && (
        <circle cx={toSvg(pendingTray.x, pendingTray.y).x}
                cy={toSvg(pendingTray.x, pendingTray.y).y}
                r="6" fill="#FFCC00" />
      )}

      {/* Equipment */}
      {items.map((it, i) => {
        const p = it.position;
        // v1.69 — use orientedDimensions for the plan footprint so 2D plan
        // matches 3D. Pre-v1.69 used `p.w_in/p.h_in` which were stale after
        // axis_map flips, causing MCC-001 to disappear or render at wrong
        // size in plan view while 3D showed it correctly.
        const _od = orientedDimensions(it);
        const planW = _od.wallSize;
        const planH = _od.thickness;
        // v1.95 — installation_side filter + exterior offset. Equipment with
        // `installation_side === "exterior"` is mounted on the OUTSIDE face
        // of its closest wall (lights, receptacles, HVAC condenser, etc).
        // `"through"` straddles the wall (HVAC pass-through, through-wall
        // PT/CT bay). Direction is inferred from which wall the item is
        // closest to in plan space.
        const instSide = it.installation_side || "interior";
        if (installSide === "interior" && instSide !== "interior") return null;
        if (installSide === "exterior" && instSide !== "exterior") return null;
        if (installSide === "basement" && instSide !== "basement") return null;
        // "all" filter hides basement too (basement equipment only shown when basement filter is active),
        // so basement items don't visually clutter the floor-plan view.
        if (installSide === "all" && instSide === "basement") return null;
        const dW_ = p.x_in;            // distance to Wall D (left, x=0)
        const dE_ = L - p.x_in - planW; // distance to Wall B (right, x=L)
        const dN_ = p.y_in;            // distance to Wall C (front, y=0)
        const dS_ = W - p.y_in - planH; // distance to Wall A (back, y=W)
        const minDist = Math.min(dW_, dE_, dN_, dS_);
        let extDx = 0, extDy = 0;
        if (instSide !== "interior") {
          // Push outward by the thickness perpendicular to the closest wall
          // (full thickness for exterior, half for through/straddle).
          const factor = instSide === "through" ? 0.5 : 1;
          if (minDist === dW_)      extDx = -planW * factor;
          else if (minDist === dE_) extDx =  planW * factor;
          else if (minDist === dN_) extDy = -planH * factor;
          else                       extDy =  planH * factor;
        }
        const xPlan = p.x_in + extDx;
        const yPlan = p.y_in + extDy;
        const tl = toSvg(xPlan, yPlan + planH);
        const rectW = planW * scale;
        const rectH = planH * scale;
        const isDragging = drag?.id === it.id && drag?.armed;
        const isSelected = selection?.kind === "equipment" && selection.id === it.id;
        const isPulsing = recentlyEdited === it.id;
        /* v1.81/82 — mounting bracket: if mounting_depth_in > 0, render a
         * second rectangle on the surface closest to the equipment. The
         * closest surface is AUTO-DETECTED across the 4 walls AND the
         * floor (using mount_height_in for the floor distance). For wall-
         * mount the bracket extends INTO the room from that wall side.
         * For floor-mount the bracket is drawn as a dashed halo around
         * the equipment rect since a horizontal pedestal isn't visible
         * top-down — the halo signals "elevated on a stand-off". */
        const mntDepth = Math.max(0, Number(it.mounting_depth_in) || 0);
        const mountInPlan = Math.max(0, Number(it.mount_height_in) || 0);
        let bracket = null;
        if (mntDepth > 0) {
          const dFloor = mountInPlan;
          const dW = p.x_in, dE = (L - p.x_in - planW);
          const dN = p.y_in, dS = (W - p.y_in - planH);
          const minD = Math.min(dFloor, dW, dE, dN, dS);
          const brkPx = mntDepth * scale;
          // v1.83 — floor pedestal is invisible top-down; skip the halo
          // entirely. Bracket renders ONLY when the closest surface is a
          // wall. (3D scene still shows the floor pedestal slab.)
          if (minD === dFloor) {
            bracket = null;
          } else if (minD === dW) {
            bracket = { x: -brkPx, y: 0, w: brkPx, h: rectH, side: "W" };
          } else if (minD === dE) {
            bracket = { x: rectW, y: 0, w: brkPx, h: rectH, side: "E" };
          } else if (minD === dN) {
            bracket = { x: 0, y: -brkPx, w: rectW, h: brkPx, side: "N" };
          } else {
            bracket = { x: 0, y: rectH, w: rectW, h: brkPx, side: "S" };
          }
        }
        /* v1.87 — animate via CSS `style.transform`, NOT the SVG attribute.
         * Prior versions (v1.80 → v1.86) set `transform="translate(x, y)"`
         * as an SVG attribute. Chromium animates these via CSS transitions,
         * but Safari, mobile WebKit and several Firefox builds DO NOT — the
         * transform snaps even though the `transition: transform …` rule is
         * present. Moving the transform into `style.transform` (with px
         * units) makes the browser treat it as a real CSS property and the
         * transition fires reliably everywhere. */
        const animMs = "600ms";
        const animEase = "cubic-bezier(0.22, 1, 0.36, 1)";
        const isPreviewing = previewPos && previewPos.id === it.id;
        const bumpScale = (isPreviewing || isPulsing) ? "scale(1.04)" : "scale(1)";
        const cssTransform = `translate(${tl.x}px, ${tl.y}px) ${bumpScale}`;
        // v1.91 — when ANY item is in the pulse window (recentlyEdited),
        // dim the OTHER items to ~40% opacity so the moving rect stands
        // out unmistakably even in big rooms full of equipment.
        const dimmed = recentlyEdited && recentlyEdited !== it.id;
        const groupOpacity = dimmed ? 0.4 : 1;
        // v1.98 — `transform-box: fill-box` + `transform-origin: center`
        // anchors the scale() bump animation to the equipment's own centre
        // in its local coord system. This is verified stable post-iter_67
        // (SNAP_RADIUS unit fix) + iter_68 (always-rendered halo). Empirical
        // measurement: 0.0000 px drift on click for ALL equipment, including
        // Wall B / D items (MOTOR-301, DISC-401, PANEL-302, HVAC-402).
        const groupStyle = isDragging
          ? { cursor: trayMode ? "crosshair" : "grab", transform: cssTransform,
              transformBox: "fill-box", transformOrigin: "center",
              opacity: groupOpacity, transition: `opacity 800ms ease-out` }
          : { cursor: trayMode ? "crosshair" : "grab",
              transform: cssTransform,
              transformBox: "fill-box", transformOrigin: "center",
              opacity: groupOpacity,
              transition: `transform ${animMs} ${animEase}, opacity 800ms ease-out` };
        // v1.98 — animate ONLY fill-opacity on the equipment rect. The
        // earlier `width`/`height`/`stroke`/`stroke-width` transitions
        // could fire on every click for tiny rects (5-10 SVG-px wide on
        // Wall B/D) because browsers re-rasterise on attribute writes
        // even when the *value* is identical, producing a half-pixel jog
        // that read as "the rect moved on click". The fill-opacity
        // transition still provides smooth selection feedback.
        const rectStyle = isDragging
          ? undefined
          : { transition: `fill-opacity ${animMs} ${animEase}` };
        return (
          <g key={`eq-${i}`}
             onMouseDown={(e) => onMouseDown(e, it)}
             style={groupStyle}
             data-testid={`plan-equip-${it.id}`}>
            {/* v1.89 — expanded hit target for tiny equipment. If the
             *  rendered rect is < 12 SVG-px on a side (typical for thin
             *  panels mounted to a wall in a large room — e.g., PNL-002
             *  at 6"×20" → 5×18 px in a 30 ft Eroom), the user struggles
             *  to land a precise click without micro-drift. An invisible
             *  halo rect 8 px around the equipment widens the clickable
             *  area so first-clicks land reliably. `pointer-events: all`
             *  + `fill: transparent` keeps the halo invisible while still
             *  receiving mouse events that bubble to the parent <g>. */}
            {(rectW < 12 || rectH < 12) && (
              <rect x={-8} y={-8} width={rectW + 16} height={rectH + 16}
                    fill="transparent" pointerEvents="all"
                    data-testid={`plan-equip-${it.id}-hit-halo`} />
            )}
            {/* Small-equipment indicator — a tiny dashed badge in the top-
             *  right corner of the equipment, only when the rect is too
             *  small to comfortably read. Doesn't capture clicks. */}
            {(rectW < 12 || rectH < 12) && (
              <g pointerEvents="none" data-testid={`plan-equip-${it.id}-small-badge`}>
                <circle cx={rectW + 4} cy={-4} r="3.5"
                        fill="#FFCC00" fillOpacity="0.9"
                        stroke="#0A0C10" strokeWidth="0.8" />
                <text x={rectW + 4} y={-1.5} fontFamily="JetBrains Mono"
                      fontSize="5" fontWeight="700" fill="#0A0C10"
                      textAnchor="middle">!</text>
              </g>
            )}
            {bracket && (
              <g data-testid={`plan-equip-${it.id}-bracket`}>
                <rect x={bracket.x} y={bracket.y} width={bracket.w} height={bracket.h}
                      fill="#FF9F0A" fillOpacity={0.18}
                      stroke="#FF9F0A" strokeWidth="1.2" strokeDasharray="4 2" />
                <line x1={bracket.x + bracket.w / 2 - 4} y1={bracket.y + bracket.h / 2}
                      x2={bracket.x + bracket.w / 2 + 4} y2={bracket.y + bracket.h / 2}
                      stroke="#FF9F0A" strokeWidth="1" />
              </g>
            )}
            {/* v1.88 — selection/pulse halo is a SEPARATE outer rect so the
                equipment rect's geometry never changes on click. Previously
                the stroke-width animated from 1.5 → 2.2 on selection, which
                visually shifted the rect by ~0.35 px on each side. For tiny
                equipment near a wall (e.g. PNL-002 at 6"×20" → 5×18 SVG-px)
                this looked exactly like "the rect moves on first click".
                v2.17 — ALWAYS render the halo (visibility-toggled, not
                conditional) so the parent <g>'s bounding box stays
                CONSTANT across selection state changes. The previous
                conditional render added/removed the halo on every click,
                which CHANGED the bbox → with `transform-box: fill-box;
                transform-origin: center`, the resolved transform origin
                shifted by 1-2 px on items near the room boundary (Wall B/
                Wall D). Browsers re-rasterised the group from the new
                origin, producing a 3-px visual drift on every click. */}
            <rect x={-2} y={-2} width={rectW + 4} height={rectH + 4}
                  fill="none"
                  stroke={isPulsing ? "#FFCC00" : "#FFCC00"}
                  strokeWidth={isPulsing ? 2.5 : 1.8}
                  strokeOpacity={(isSelected || isPulsing) ? (isPulsing ? 1 : 0.85) : 0}
                  pointerEvents="none"
                  style={{ transition: "stroke-width 240ms cubic-bezier(0.22, 1, 0.36, 1), stroke-opacity 240ms ease-out" }} />
            <rect x={0} y={0} width={rectW} height={rectH}
              fill={
                instSide === "exterior" ? "#FF9F0A"
                : instSide === "through" ? "#FF66E0"
                : instSide === "basement" ? "#A78BFA"
                : "#00E5FF"}
              fillOpacity={isDragging ? 0.35 : (isPulsing ? 0.5 : (isSelected ? 0.28 : 0.18))}
              stroke={
                instSide === "exterior" ? "#FF9F0A"
                : instSide === "through" ? "#FF66E0"
                : instSide === "basement" ? "#A78BFA"
                : "#00E5FF"}
              strokeWidth={1.5}
              strokeDasharray={instSide === "through" ? "5 3" : (instSide === "basement" ? "4 2" : undefined)}
              style={rectStyle} />
            <text x={4} y={14} fill="#fff" fontFamily="JetBrains Mono" fontSize="11">
              {it.tag || it.equipment_type?.slice(0, 12) || `EQ-${i + 1}`}
            </text>
            <text x={4} y={27} fill="#9CA3AF" fontFamily="JetBrains Mono" fontSize="9">
              {`${fmtLenShort(planW, project)} × ${fmtLenShort(planH, project)}`}
            </text>
            {instSide !== "interior" && (
              <text x={rectW - 4} y={14} fill={
                instSide === "exterior" ? "#FF9F0A"
                : instSide === "basement" ? "#A78BFA"
                : "#FF66E0"}
                    fontFamily="JetBrains Mono" fontSize="8" textAnchor="end" fontWeight="700">
                {instSide === "exterior" ? "EXT" : instSide === "basement" ? "BSMT" : "THRU"}
              </text>
            )}
          </g>
        );
      })}

      <g transform={`translate(${padding}, ${viewH - 4})`}>
        <rect x="0" y="-14" width="14" height="10" fill="#00E5FF" fillOpacity="0.18" stroke="#00E5FF" />
        <text x="20" y="-6" fill="#9CA3AF" fontFamily="JetBrains Mono" fontSize="10">Interior</text>
        <rect x="85" y="-14" width="14" height="10" fill="#FF9F0A" fillOpacity="0.18" stroke="#FF9F0A" />
        <text x="105" y="-6" fill="#9CA3AF" fontFamily="JetBrains Mono" fontSize="10">Exterior</text>
        <rect x="170" y="-14" width="14" height="10" fill="#FF66E0" fillOpacity="0.18" stroke="#FF66E0" strokeDasharray="3 2" />
        <text x="190" y="-6" fill="#9CA3AF" fontFamily="JetBrains Mono" fontSize="10">Through</text>
        <rect x="250" y="-14" width="14" height="10" fill="#A78BFA" fillOpacity="0.18" stroke="#A78BFA" strokeDasharray="4 2" />
        <text x="270" y="-6" fill="#9CA3AF" fontFamily="JetBrains Mono" fontSize="10">Basement</text>
        <rect x="335" y="-14" width="14" height="10" fill="url(#hatch)" stroke="#FFCC00" strokeOpacity="0.6" />
        <text x="355" y="-6" fill="#9CA3AF" fontFamily="JetBrains Mono" fontSize="10">110.26</text>
        <line x1="405" y1="-9" x2="430" y2="-9" stroke="#FFA500" strokeWidth="4" />
        <text x="435" y="-6" fill="#9CA3AF" fontFamily="JetBrains Mono" fontSize="10">Cable tray</text>
      </g>

      {/* Smart guides during drag */}
      {drag && (guides.x || guides.y) && (
        <g pointerEvents="none" data-testid="plan-smart-guides">
          {guides.x && (
            <>
              <line x1={toSvg(guides.x.v, 0).x} y1={toSvg(0, 0).y}
                    x2={toSvg(guides.x.v, 0).x} y2={toSvg(0, W).y}
                    stroke="#FF66E0" strokeWidth="1.2" strokeDasharray="5 3" />
              <rect x={toSvg(guides.x.v, W / 2).x - 38}
                    y={toSvg(0, W / 2).y - 8}
                    width="76" height="14"
                    fill="rgba(10,13,18,0.95)" stroke="#FF66E0" strokeWidth="0.8" />
              <text x={toSvg(guides.x.v, W / 2).x}
                    y={toSvg(0, W / 2).y + 3}
                    fill="#FF66E0" fontFamily="JetBrains Mono"
                    fontSize="10" textAnchor="middle" fontWeight="700">
                ↕ {guides.x.tag}
              </text>
            </>
          )}
          {guides.y && (
            <>
              <line x1={toSvg(0, guides.y.v).x} y1={toSvg(0, guides.y.v).y}
                    x2={toSvg(L, guides.y.v).x} y2={toSvg(0, guides.y.v).y}
                    stroke="#FF66E0" strokeWidth="1.2" strokeDasharray="5 3" />
              <rect x={toSvg(L / 2, guides.y.v).x - 38}
                    y={toSvg(0, guides.y.v).y - 8}
                    width="76" height="14"
                    fill="rgba(10,13,18,0.95)" stroke="#FF66E0" strokeWidth="0.8" />
              <text x={toSvg(L / 2, guides.y.v).x}
                    y={toSvg(0, guides.y.v).y + 3}
                    fill="#FF66E0" fontFamily="JetBrains Mono"
                    fontSize="10" textAnchor="middle" fontWeight="700">
                ↔ {guides.y.tag}
              </text>
            </>
          )}
        </g>
      )}

      {/* Dimension overlay */}
      {showDims && selection?.id && (() => {
        const room = { length_in: L, width_in: W, depth_in: project.depth_in || 96 };
        if (selection.kind === "equipment") {
          const it = items.find((x) => x.id === selection.id);
          if (!it) return null;
          return (
            <PlanDimensions
              selectedItem={it} selectedKind="equipment"
              room={room} allItems={items} allTrays={trays}
              toSvg={toSvg} onEditDimension={onEditDimension}
              onPreviewDimension={onPreviewDimension}
              project={project} />
          );
        }
        if (selection.kind === "tray") {
          const tray = trays.find((x) => x.id === selection.id);
          if (!tray) return null;
          return (
            <PlanDimensions
              selectedItem={tray} selectedKind="tray"
              room={room} allItems={items} allTrays={trays}
              toSvg={toSvg} onEditDimension={onEditDimension}
              onPreviewDimension={onPreviewDimension}
              project={project} />
          );
        }
        return null;
      })()}

      {/* v2.10 — 2D plan view shell border REMOVED per user request.
          The thick gray frame around the room represented wall thickness,
          which is already shown in 3D and elevation views. In 2D plan we
          just need the cyan room boundary line below. */}
      <g data-testid="plan-enclosure-shell" />
    </svg>
  );
}
